import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  formatTaskRef,
  taskInterventionStart,
  taskStatusLabelFr,
  TASK_TYPE_LABELS_FR,
  type Task,
} from "@relais/shared";
import { STATUS_ACCENT } from "@relais/design-system";
import { useSession, useUserProfile } from "../lib/auth.js";
import { useOffline } from "../lib/offline-context.js";
import { TaskSyncPill } from "../features/task-detail/TaskSyncPill.js";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

function Dashboard() {
  const session = useSession();
  const profile = useUserProfile();
  const { service, tick } = useOffline();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!service) return;
    let active = true;
    service.listTasks().then((t) => {
      if (!active) return;
      setTasks(t);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [service, tick]);

  if (session.status === "loading") return <p>…</p>;
  if (session.status === "anon") return <Navigate to="/login" />;

  if (!loaded) return <p className="text-muted-foreground">Chargement…</p>;

  const questioned = tasks.filter((t) => t.status === "questioned");
  const todo = tasks.filter((t) =>
    ["assigned", "accepted"].includes(t.status),
  );
  const inProgress = tasks.filter((t) => t.status === "in_progress");
  const submitted = tasks.filter((t) => t.status === "submitted");
  const completed = tasks.filter((t) => t.status === "completed");

  const greetingName =
    profile.status === "ready" ? profile.profile.first_name : null;

  return (
    <div className="space-y-8">
      {greetingName && (
        <h1 className="text-xl font-semibold">Bonjour {greetingName}</h1>
      )}
      <Section title="À corriger" tasks={questioned} />
      <Section title="À traiter" tasks={todo} />
      <Section title="En cours" tasks={inProgress} />
      <Section title="Soumises" tasks={submitted} />
      <Section title="Terminées" tasks={completed} />
    </div>
  );
}

function Section({ title, tasks }: { title: string; tasks: Task[] }) {
  return (
    <section>
      <h2 className="text-base font-semibold mb-2">
        {title} ({tasks.length})
      </h2>
      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">Rien à afficher.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {tasks.map((t) => (
            <li key={t.id}>
              <Link
                to="/tasks/$taskId"
                params={{ taskId: t.id }}
                className="block px-4 py-3 hover:bg-muted"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {TASK_TYPE_LABELS_FR[t.type]} ·{" "}
                    {t.project_snapshot?.name ?? t.project_id}
                  </span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <TaskSyncPill taskId={t.id} />
                    <span
                      className={
                        "rounded px-2 py-0.5 text-xs font-medium " +
                        STATUS_ACCENT[t.status].badge
                      }
                    >
                      {taskStatusLabelFr(t, { short: true })}
                    </span>
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  {/* Référence des emails et SMS de relance : elle doit être
                      lisible SANS ouvrir la mission, sinon retrouver la bonne
                      depuis un SMS demande d'ouvrir chaque fiche. */}
                  <span className="font-mono">{formatTaskRef(t.id)}</span>
                  {taskInterventionStart(t) && (
                    <span>{new Date(taskInterventionStart(t)!).toLocaleString("fr-FR")}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
